import { MaterialCommunityIcons } from "@expo/vector-icons";
import { manipulateAsync, SaveFormat } from "expo-image-manipulator";
import * as ImagePicker from "expo-image-picker";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Alert,
    Image,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { ProfilePhotoSheet, type ProfilePhotoAction } from "@/components/id-card/ProfilePhotoSheet";
import {
    fetchRnIdCard,
    removeProfilePhoto,
    requestProfilePhotoUploadUrl,
    setProfilePhoto,
    type IdCardAgency,
    type IdCardResponse
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logRocketRedactProps } from "@/lib/logrocket";
import { colors, fonts, gradients, radius, shadows, spacing, typography } from "@/lib/theme";
import { uploadFromUri } from "@/lib/upload-from-uri";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Inputs are ISO date strings (YYYY-MM-DD). Parsing manually avoids the
// timezone shift that `new Date("YYYY-MM-DD")` would introduce.
function formatIsoDate(iso: string | null): string {
    if (iso == null) {
        return "—";
    }
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
        return iso;
    }
    const [, year, month, day] = match;
    const monthIdx = Number(month) - 1;
    if (monthIdx < 0 || monthIdx > 11) {
        return iso;
    }
    return `${MONTHS[monthIdx]} ${Number(day)}, ${year}`;
}

function initialFromName(fullName: string): string {
    const trimmed = fullName.trim();
    if (trimmed === "") {
        return "?";
    }
    return trimmed[0].toUpperCase();
}

export default function IdCardScreen() {
    const router = useRouter();
    const { user } = useAuth();
    const [data, setData] = useState<IdCardResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedAgencyId, setSelectedAgencyId] = useState<number | null>(null);
    const [sheetVisible, setSheetVisible] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [photoError, setPhotoError] = useState<string | null>(null);

    const loadIdCard = useCallback(async () => {
        const res = await fetchRnIdCard();
        setData(res);
        // The agencies list is already scoped to the nurse's visits TODAY. When
        // there's exactly one, skip the picker and show its card directly. With
        // two or more, leave the selection null so the picker shows first. A
        // refetch (e.g. after a photo change) keeps the agency already chosen.
        setSelectedAgencyId(
            (prev) => prev ?? (res.agencies.length === 1 ? res.agencies[0].id : null)
        );
        return res;
    }, []);

    useEffect(() => {
        let cancelled = false;
        loadIdCard().catch((e) => {
            if (cancelled) {
                return;
            }
            setError(e instanceof Error ? e.message : "Failed to load ID card");
        });
        return () => {
            cancelled = true;
        };
    }, [loadIdCard]);

    // Take/choose a new photo: pick (square crop) → normalize to JPEG → presigned
    // PUT to S3 → confirm → refetch so the card shows the new avatar.
    const pickAndUpload = useCallback(
        async (source: "camera" | "photo") => {
            const perm =
                source === "camera"
                    ? await ImagePicker.requestCameraPermissionsAsync()
                    : await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (!perm.granted) {
                setPhotoError(
                    source === "camera"
                        ? "Camera access is needed. Grant it in Settings to take a photo."
                        : "Photo access is needed. Grant it in Settings to choose a photo."
                );
                return;
            }
            const opts: ImagePicker.ImagePickerOptions = {
                mediaTypes: ["images"],
                allowsEditing: true,
                aspect: [1, 1],
                quality: 0.8
            };
            const result =
                source === "camera"
                    ? await ImagePicker.launchCameraAsync(opts)
                    : await ImagePicker.launchImageLibraryAsync(opts);
            if (result.canceled || result.assets.length === 0) {
                return;
            }

            setUploading(true);
            setPhotoError(null);
            try {
                // HEIC → JPEG (the API + browsers expect JPEG) and downscale to a
                // 512px avatar — it's only ever shown small, so storing/serving the
                // full-resolution capture just slows every fetch. The picker already
                // square-crops (aspect 1:1), so width=512 yields ~512×512.
                const jpeg = await manipulateAsync(
                    result.assets[0].uri,
                    [{ resize: { width: 512 } }],
                    {
                        compress: 0.8,
                        format: SaveFormat.JPEG
                    }
                );
                const { uploadUrl, s3Url } = await requestProfilePhotoUploadUrl(
                    `profile-${Date.now()}.jpg`,
                    "image/jpeg"
                );
                await uploadFromUri({ uri: jpeg.uri, uploadUrl, contentType: "image/jpeg" });
                await setProfilePhoto(s3Url);
                await loadIdCard();
            } catch (e) {
                setPhotoError(e instanceof Error ? e.message : "Couldn't update your photo");
            } finally {
                setUploading(false);
            }
        },
        [loadIdCard]
    );

    const removePhoto = useCallback(() => {
        Alert.alert("Remove photo", "Remove your profile photo?", [
            { text: "Cancel", style: "cancel" },
            {
                text: "Remove",
                style: "destructive",
                onPress: async () => {
                    setUploading(true);
                    setPhotoError(null);
                    try {
                        await removeProfilePhoto();
                        await loadIdCard();
                    } catch (e) {
                        setPhotoError(
                            e instanceof Error ? e.message : "Couldn't remove your photo"
                        );
                    } finally {
                        setUploading(false);
                    }
                }
            }
        ]);
    }, [loadIdCard]);

    const onSheetPick = useCallback(
        (action: ProfilePhotoAction) => {
            if (action === "remove") {
                removePhoto();
            } else {
                void pickAndUpload(action);
            }
        },
        [pickAndUpload, removePhoto]
    );

    const selectedAgency = useMemo<IdCardAgency | null>(() => {
        if (data == null || selectedAgencyId == null) {
            return null;
        }
        return data.agencies.find((a) => a.id === selectedAgencyId) ?? null;
    }, [data, selectedAgencyId]);

    // A nurse visiting more than one agency today picks which one the card is
    // issued "on behalf of" first. With exactly one agency (auto-selected above)
    // or none, there is nothing to pick — the card shows directly.
    const showingPicker = data != null && data.agencies.length > 1 && selectedAgencyId == null;

    const goBack = useCallback(() => {
        // When there are several agencies to choose from, "back" from the card
        // returns to the picker so the nurse can switch; otherwise it leaves.
        if (selectedAgencyId != null && data != null && data.agencies.length > 1) {
            setSelectedAgencyId(null);
            setPhotoError(null);
            return;
        }
        if (router.canGoBack()) {
            router.back();
        } else {
            router.replace("/(tabs)");
        }
    }, [router, selectedAgencyId, data]);

    return (
        <SafeAreaView style={styles.safe} edges={["top", "left", "right"]}>
            <View style={styles.headerRow}>
                <TouchableOpacity
                    onPress={goBack}
                    style={styles.backButton}
                    accessibilityLabel="Go back"
                >
                    <MaterialCommunityIcons
                        name="chevron-left"
                        size={28}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>
                    {showingPicker ? "Select agency" : "Virtual ID card"}
                </Text>
                <View style={styles.backButton} />
            </View>

            {data == null && error == null ? (
                <View style={styles.center}>
                    <ActivityIndicator size="large" color={colors.teal400} />
                </View>
            ) : error != null ? (
                <View style={styles.center}>
                    <Text style={styles.errorText}>{error}</Text>
                </View>
            ) : data != null && showingPicker ? (
                <ScrollView contentContainerStyle={styles.pickerContent}>
                    <Text style={styles.pickerTitle}>Choose an agency</Text>
                    <Text style={styles.pickerSubtitle}>
                        You have visits with more than one agency today. Pick which to show on your
                        ID card.
                    </Text>
                    {data.agencies.map((agency) => (
                        <TouchableOpacity
                            key={agency.id}
                            onPress={() => {
                                setSelectedAgencyId(agency.id);
                                setPhotoError(null);
                            }}
                            activeOpacity={0.7}
                            style={styles.agencyRow}
                            accessibilityRole="button"
                            accessibilityLabel={`Show ID card for ${agency.name}`}
                        >
                            {agency.logoUrl != null ? (
                                <Image
                                    source={{ uri: agency.logoUrl }}
                                    style={styles.agencyRowLogo}
                                    resizeMode="contain"
                                />
                            ) : (
                                <View style={styles.agencyRowLogoFallback}>
                                    <MaterialCommunityIcons
                                        name="office-building-outline"
                                        size={24}
                                        color={colors.textTertiary}
                                    />
                                </View>
                            )}
                            <Text style={styles.agencyRowName} numberOfLines={2}>
                                {agency.name}
                            </Text>
                            <MaterialCommunityIcons
                                name="chevron-right"
                                size={24}
                                color={colors.textTertiary}
                            />
                        </TouchableOpacity>
                    ))}
                </ScrollView>
            ) : data != null ? (
                <ScrollView contentContainerStyle={styles.scrollContent}>
                    <View style={styles.card}>
                        <LinearGradient
                            colors={gradients.brand}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.cardBand}
                        />
                        <TouchableOpacity
                            style={styles.avatarWrap}
                            activeOpacity={0.85}
                            onPress={() => setSheetVisible(true)}
                            disabled={uploading}
                            accessibilityRole="button"
                            accessibilityLabel="Change profile photo"
                        >
                            {data.photoUrl != null ? (
                                <Image
                                    {...logRocketRedactProps()}
                                    source={{ uri: data.photoUrl }}
                                    style={styles.avatarImage}
                                />
                            ) : (
                                <View style={styles.avatarFallback}>
                                    <Text style={styles.avatarFallbackText}>
                                        {initialFromName(
                                            data.fullName.length > 0
                                                ? data.fullName
                                                : (user?.firstName ?? "?")
                                        )}
                                    </Text>
                                </View>
                            )}
                            {uploading ? (
                                <View style={styles.avatarOverlay}>
                                    <ActivityIndicator color={colors.surface} />
                                </View>
                            ) : (
                                <View style={styles.avatarEditBadge}>
                                    <MaterialCommunityIcons
                                        name="camera"
                                        size={16}
                                        color={colors.surface}
                                    />
                                </View>
                            )}
                        </TouchableOpacity>

                        <View style={styles.identityBlock}>
                            <Text style={styles.name}>{data.fullName}</Text>
                            <Text style={styles.roleLabel}>{data.role}</Text>
                            {photoError != null ? (
                                <Text style={styles.photoError}>{photoError}</Text>
                            ) : null}
                        </View>

                        <View style={styles.infoTable}>
                            <InfoRow label="ID" value={`#${data.rnId}`} />
                            <InfoRow label="License number" value={data.licenseNumber ?? "—"} />
                            <InfoRow label="Gender" value={data.gender ?? "—"} />
                            <InfoRow label="Date of birth" value={formatIsoDate(data.dob)} />
                            <InfoRow label="Hire date" value={formatIsoDate(data.hireDate)} />
                        </View>

                        {selectedAgency != null && (
                            <View style={styles.agencyBlock}>
                                <View style={styles.divider} />
                                <Text style={styles.agencyLabel}>On behalf of</Text>
                                <View style={styles.agencyPanel}>
                                    {selectedAgency.logoUrl != null ? (
                                        <Image
                                            source={{ uri: selectedAgency.logoUrl }}
                                            style={styles.agencyLogo}
                                            resizeMode="contain"
                                        />
                                    ) : (
                                        <View style={styles.agencyLogoFallback}>
                                            <MaterialCommunityIcons
                                                name="office-building-outline"
                                                size={28}
                                                color={colors.textTertiary}
                                            />
                                        </View>
                                    )}
                                    <Text style={styles.agencyName}>{selectedAgency.name}</Text>
                                </View>
                            </View>
                        )}

                        <Text style={styles.poweredBy}>Powered by TaskHealth</Text>
                    </View>
                </ScrollView>
            ) : null}

            <ProfilePhotoSheet
                visible={sheetVisible}
                onClose={() => setSheetVisible(false)}
                onPick={onSheetPick}
                canRemove={data?.photoUrl != null}
            />
        </SafeAreaView>
    );
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>{label}</Text>
            <Text style={styles.infoValue}>{value}</Text>
        </View>
    );
}

const AVATAR_SIZE = 120;
const BAND_HEIGHT = 140;

const styles = StyleSheet.create({
    safe: {
        flex: 1,
        backgroundColor: colors.background
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.sm
    },
    backButton: {
        width: 40,
        height: 40,
        alignItems: "center",
        justifyContent: "center"
    },
    headerTitle: {
        ...typography.h2,
        color: colors.textPrimary
    },
    center: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center"
    },
    errorText: {
        ...typography.body,
        color: colors.error600,
        textAlign: "center",
        paddingHorizontal: spacing.lg
    },
    // ── Agency picker (shown before the card) ──
    pickerContent: {
        paddingHorizontal: spacing.base,
        paddingTop: spacing.sm,
        paddingBottom: spacing["2xl"]
    },
    pickerTitle: {
        ...typography.h1,
        color: colors.textPrimary,
        marginBottom: spacing.xs
    },
    pickerSubtitle: {
        ...typography.body,
        color: colors.textSecondary,
        marginBottom: spacing.lg
    },
    agencyRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.base,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        paddingVertical: spacing.base,
        paddingHorizontal: spacing.base,
        marginBottom: spacing.md,
        ...shadows.sm
    },
    agencyRowLogo: {
        width: 48,
        height: 40,
        borderRadius: radius.sm
    },
    agencyRowLogoFallback: {
        width: 48,
        height: 40,
        borderRadius: radius.sm,
        backgroundColor: colors.gray100,
        alignItems: "center",
        justifyContent: "center"
    },
    agencyRowName: {
        ...typography.subtitle,
        color: colors.textPrimary,
        flex: 1
    },
    scrollContent: {
        paddingHorizontal: spacing.base,
        paddingBottom: spacing["2xl"]
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        overflow: "hidden",
        ...shadows.lg
    },
    cardBand: {
        height: BAND_HEIGHT
    },
    avatarWrap: {
        alignSelf: "center",
        marginTop: -AVATAR_SIZE / 2,
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: AVATAR_SIZE / 2,
        backgroundColor: colors.surface,
        padding: 4,
        ...shadows.md
    },
    avatarImage: {
        width: "100%",
        height: "100%",
        borderRadius: AVATAR_SIZE / 2
    },
    avatarOverlay: {
        ...StyleSheet.absoluteFill,
        margin: 4,
        borderRadius: AVATAR_SIZE / 2,
        backgroundColor: "rgba(0,0,0,0.4)",
        alignItems: "center",
        justifyContent: "center"
    },
    avatarEditBadge: {
        position: "absolute",
        right: 2,
        bottom: 2,
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: colors.teal500,
        borderWidth: 2,
        borderColor: colors.surface,
        alignItems: "center",
        justifyContent: "center"
    },
    photoError: {
        ...typography.caption,
        color: colors.error600,
        textAlign: "center",
        marginTop: spacing.xs
    },
    avatarFallback: {
        width: "100%",
        height: "100%",
        borderRadius: AVATAR_SIZE / 2,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    avatarFallbackText: {
        fontSize: 44,
        fontFamily: fonts.bold,
        color: colors.teal600
    },
    identityBlock: {
        alignItems: "center",
        paddingTop: spacing.base,
        paddingHorizontal: spacing.lg
    },
    name: {
        ...typography.h1,
        color: colors.textPrimary,
        textAlign: "center"
    },
    roleLabel: {
        ...typography.subtitle,
        color: colors.teal600,
        marginTop: spacing.xs
    },
    infoTable: {
        paddingHorizontal: spacing.xl,
        paddingTop: spacing.lg,
        paddingBottom: spacing.base
    },
    infoRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: spacing.sm
    },
    infoLabel: {
        ...typography.subtitle,
        color: colors.textSecondary
    },
    infoValue: {
        ...typography.body,
        color: colors.textPrimary,
        fontFamily: fonts.semiBold
    },
    agencyBlock: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.sm,
        paddingBottom: spacing.lg
    },
    divider: {
        height: 1,
        backgroundColor: colors.border,
        marginBottom: spacing.base
    },
    agencyLabel: {
        ...typography.label,
        color: colors.textTertiary,
        textTransform: "uppercase",
        marginBottom: spacing.sm
    },
    agencyPanel: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.base
    },
    agencyLogo: {
        width: 56,
        height: 48,
        borderRadius: radius.sm
    },
    agencyLogoFallback: {
        width: 56,
        height: 48,
        borderRadius: radius.sm,
        backgroundColor: colors.gray100,
        alignItems: "center",
        justifyContent: "center"
    },
    agencyName: {
        ...typography.h2,
        color: colors.textPrimary,
        flexShrink: 1
    },
    poweredBy: {
        ...typography.caption,
        color: colors.textTertiary,
        textAlign: "center",
        paddingBottom: spacing.lg
    }
});
